/* eslint-disable jsx-a11y/anchor-is-valid */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { useCallback, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";

import { useModalContext } from "../../providers";
import { filterTypes } from "./input";
import { Item } from "./item";
import { DropdownFilter, InputFilterFind } from "./components";

type PropsListFilter = {
  initialFilter?: string;
  contentFilter?: string | null;
  searchTitle?: string;
  configEnabledFilters?: object;
  configFixedFilters?: object;
  onFilter?: (formatFilter, isMounted) => void;
  onReload?: () => void;
  cols?: number;
  children?: any;
};

export function ListFilter({
  initialFilter = '',
  configEnabledFilters = {},
  configFixedFilters = {},
  contentFilter = null,
  searchTitle = 'Search',
  onFilter = () => null,
  onReload = () => null,
  cols = 12,
  children
}: PropsListFilter) {
  const currentPath = useLocation().pathname;
  const { openModal } = useModalContext();

  const [filters, setFilters] = useState({});
  const [isMounted, setIsMounted] = useState(false);
  const [currentFilterPath, setCurrentFilterPath] = useState(currentPath);

  const applyFilters = useCallback(
    (applicableFilters = filters) => {
      const filterFormat = Object.entries(applicableFilters).map(([objectKey, filter]: any[]) => {
        const key = configEnabledFilters[objectKey]?.field || objectKey;
        const filterType = configEnabledFilters[key]?.type || filter.type || filterTypes.INPUT;
        if (filterType === filterTypes.DATE_RANGE) {
          return `${key}:${filter.values.join('--')}`;
        }
        if (filterType === filterTypes.CONTENT) {
          return `${key}$${filter.values.join('--')}`;
        }
        if (
          filterType === filterTypes.SELECT_PATIENT ||
          filterType === filterTypes.SELECT_USER ||
          filterType === filterTypes.SELECT_DATA_IN_EXAMS ||
          filterType === filterTypes.SELECT_OBJECT_IN_EXAMS
        ) {
          return `${key}:${filter.values[0].id}`;
        }
        return `${key}:${filter.values?.join(',')}`;
      });
      const formatFilter = filterFormat?.join('|');
      onFilter(formatFilter, isMounted);
    },
    [configEnabledFilters, filters, isMounted, onFilter]
  );

  const setDefaultFilters = useCallback(() => {
    const initialFilters = (initialFilter || '').split('|').reduce((acc, value) => {
      if (!value) {
        return acc;
      }
      const separador = value.indexOf(':') >= 0 ? ':' : '$';
      const [name, val] = value.split(separador);
      const values = val.indexOf(',') > 0 ? val.split(',') : [val];
      let filter = {
        id: name,
        field: name,
        values: values,
        type: '',
        isFixed: false
      };
      if (contentFilter && name === contentFilter) {
        filter = {
          ...filter,
          type: filterTypes.CONTENT,
          isFixed: true
        };
      }
      acc[name] = filter;
      return acc;
    }, {});

    const fixedFilters = Object.entries(configFixedFilters).reduce((acc, [key, item]) => {
      if (!acc[key] || item.isFixed) {
        acc[key] = {
          id: key,
          field: key,
          values: item.values,
          isFixed: item.isFixed
        };
      }
      return acc;
    }, initialFilters);
    setFilters(fixedFilters);
    applyFilters(fixedFilters);
  }, [applyFilters, configFixedFilters, contentFilter, initialFilter]);

  const addFilter = useCallback(
    (newFilter) => {
      const newFilters = {
        ...filters,
        [newFilter.field]: newFilter
      };
      setFilters(newFilters);
      applyFilters(newFilters);
    },
    [applyFilters, filters]
  );

  const removeFilter = useCallback(
    (filter) => {
      const newFilter = { ...filters };
      delete newFilter[filter.field];
      setFilters(newFilter);
      applyFilters(newFilter);
    },
    [applyFilters, filters]
  );

  const openModalFilter = useCallback(
    (filterKey) => {
      openModal('filters-modal', {
        config: configEnabledFilters[filterKey],
        field: filterKey,
        actualValue: filters[filterKey]?.values,
        addFilter
      });
    },
    [addFilter, configEnabledFilters, filters, openModal]
  );

  useEffect(() => {
    if (!isMounted || currentFilterPath !== currentPath) {
      setCurrentFilterPath(currentPath);
      setDefaultFilters();
      setIsMounted(true);
    }
  }, [isMounted, setDefaultFilters, currentFilterPath, currentPath]);

  return (
    <div className="row">
      <div className={`col-${cols} d-flex justify-content-between`}>
        <DropdownFilter
          filters={filters}
          configEnabledFilters={configEnabledFilters}
          openModalFilter={openModalFilter}
        />
        {contentFilter && (
          <InputFilterFind
            searchTitle={searchTitle}
            contentFilter={contentFilter}
            removeFilter={removeFilter}
            addFilter={addFilter}
          />
        )}
      </div>
      {children && <div className={`col-${12 - cols} mt-1`}>{children}</div>}
      <div className="col-12 mt-1">
        <div className="row row-cols-auto justify-content-start">
          {Object.entries(filters).map(([key, applyFilter]: any[]) => {
            if (!applyFilter.isFixed && configEnabledFilters[key]) {
              return (
                <Item
                  key={key}
                  className="col-auto p-0 m-0 mb-1"
                  filter={applyFilter}
                  config={configEnabledFilters[key]}
                  onRemoveFilter={removeFilter}
                  onClick={() => openModalFilter(key)}
                />
              );
            }
            return null;
          })}
        </div>
      </div>
    </div>
  );
}

export default ListFilter;
